/**
 * client/src/hooks/useRazorpayCheckout.ts
 *
 * Embedded Razorpay checkout hook for billing plan upgrades.
 *
 * Flow:
 *  1. POST /api/billing/create-order  → { orderId, amount, currency, key, planCode, planName }
 *  2. Load Razorpay checkout.js and open the modal with order_id
 *  3. User completes payment inside the modal
 *  4. POST /api/billing/verify-payment with { razorpay_payment_id, razorpay_order_id,
 *       razorpay_signature, planCode } — server verifies HMAC-SHA256 and upgrades plan
 *  5. Invalidate subscription + auth + generation-limit queries so the UI refreshes
 */

import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { loadRazorpayScript, RAZORPAY_KEY } from "@/lib/razorpay";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";

export type CheckoutPlanId = "pro" | "enterprise";

interface CreateOrderResponse {
  orderId: string;
  amount: number;
  currency: string;
  key: string;
  planCode: "PRO" | "ENTERPRISE";
  planName: string;
}

interface VerifyPaymentResponse {
  success: boolean;
  planCode: "PRO" | "ENTERPRISE";
}

export function useRazorpayCheckout() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  /**
   * Open the Razorpay embedded checkout for the given plan.
   * Resolves when payment is verified; throws on failure or dismissal.
   */
  const initiateCheckout = async (planId: CheckoutPlanId): Promise<void> => {
    if (!user) {
      toast({ title: "Please sign in to upgrade your plan", variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      // 1. Load Razorpay checkout.js (cached if already loaded)
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        throw new Error("Failed to load payment script. Please check your internet connection.");
      }

      // 2. Create a Razorpay order on the server
      const orderData: CreateOrderResponse = await apiRequest(
        "/api/billing/create-order",
        "POST",
        { planCode: planId },
      ).then((r) => r.json());

      // 3. Open the Razorpay checkout modal
      await new Promise<void>((resolve, reject) => {
        const rzp = new window.Razorpay({
          key: orderData.key || RAZORPAY_KEY,
          amount: orderData.amount,
          currency: orderData.currency,
          order_id: orderData.orderId,
          name: "StoryLikho",
          description: `${orderData.planName} — Monthly Subscription`,
          notes: {
            planCode: orderData.planCode,
            userId: String(user.id),
          },
          prefill: {
            email: user.email ?? undefined,
            name: [user.firstName, user.lastName].filter(Boolean).join(" ") || undefined,
          },
          theme: { color: "#6366f1" },

          // 4. On successful payment, verify the signature server-side
          handler: async (response: {
            razorpay_payment_id: string;
            razorpay_order_id: string;
            razorpay_signature: string;
          }) => {
            try {
              const result: VerifyPaymentResponse = await apiRequest(
                "/api/billing/verify-payment",
                "POST",
                {
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_signature: response.razorpay_signature,
                  planCode: orderData.planCode,
                },
              ).then((r) => r.json());

              if (!result.success) {
                throw new Error("Payment could not be verified. Please contact support.");
              }

              // 5. Refresh all billing-related queries
              queryClient.invalidateQueries({ queryKey: ["/api/billing/subscription"] });
              queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
              queryClient.invalidateQueries({ queryKey: ["/api/generation-limit"] });

              toast({
                title: "Payment successful!",
                description: `You're now on the ${orderData.planName} plan. Enjoy unlimited generations!`,
              });

              resolve();
            } catch (err: any) {
              reject(err);
            }
          },

          modal: {
            ondismiss: () => {
              // User closed the modal without paying — not an error, just cancel
              reject(new Error("__dismissed__"));
            },
          },
        });

        rzp.open();
      });
    } catch (err: any) {
      if (err.message !== "__dismissed__") {
        console.error("Razorpay checkout error:", err);
        toast({
          title: "Payment failed",
          description: err.message ?? "An unexpected error occurred. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return { initiateCheckout, loading };
}
